/**
 * Analytics API Client
 *
 * Handles all P3 analytics API calls:
 * - Three R's Classification
 * - Outcome Correlation
 * - Calibration Monitor
 * - Health Predictor
 * - System Intelligence Synthesis
 *
 * Copyright (c) 2026 Equilateral AI. All rights reserved.
 * Follows: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, type TimComboAPIResponse } from './apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

export type { TimComboAPIResponse };

// =============================================================================
// THREE R'S CLASSIFICATION
// =============================================================================

export interface ThreeRsClassification {
  Agent_Name: string;
  Category: 'REPLACE' | 'REMEDIATE' | 'REWARD';
  Percentile: number;
  Batting_Average: number;
  Classified_At: string;
}

export async function getClassifications(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.CLASSIFICATION,
    'GET',
    null,
    { params: { Client_ID: clientId } }
  );
}

export async function triggerClassification(
  clientId: string,
  data: { Agent_Names?: string[]; Force_Refresh?: boolean } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.CLASSIFICATION_TRIGGER,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

// =============================================================================
// OUTCOME CORRELATION
// =============================================================================

export interface OutcomeCorrelation {
  Factor_A: string;
  Factor_B: string;
  Correlation_Coefficient: number;
  Sample_Size: number;
  P_Value: number;
}

export async function getCorrelations(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.CORRELATIONS,
    'GET',
    null,
    { params: { Client_ID: clientId } }
  );
}

export async function runCorrelation(
  clientId: string,
  data: { Factor_Types?: string[]; Min_Sample_Size?: number } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.CORRELATION_RUN,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

export async function getCorrelationTrends(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.CORRELATION_TRENDS,
    'GET',
    null,
    { params: { Client_ID: clientId } }
  );
}

// =============================================================================
// CALIBRATION MONITOR
// =============================================================================

export interface CalibrationScore {
  Agent_Name: string;
  Brier_Score: number;
  Overconfidence_Ratio: number;
  Grade: 'A' | 'B' | 'C' | 'D' | 'F';
  Drift_Detected: boolean;
}

export async function getCalibrationScores(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.CALIBRATION_SCORES,
    'GET',
    null,
    { params: { Client_ID: clientId } }
  );
}

export async function runCalibration(
  clientId: string,
  data: { Agent_Names?: string[]; Window_Days?: number } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.CALIBRATION_RUN,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

// =============================================================================
// HEALTH PREDICTOR
// =============================================================================

export interface HealthSignal {
  Signal_Name: string;
  Score: number;
}

export interface HealthPrediction {
  Agent_Name: string;
  Health_Score: number;
  Health_Status: 'healthy' | 'warning' | 'at_risk' | 'critical';
  Signals: HealthSignal[];
  Trend: 'improving' | 'stable' | 'declining';
}

export async function getHealthPredictions(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.HEALTH_PREDICTIONS,
    'GET',
    null,
    { params: { Client_ID: clientId } }
  );
}

export async function getHealthSignals(clientId: string, agentName: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.HEALTH_SIGNALS,
    'GET',
    null,
    { params: { Client_ID: clientId, Agent_Name: agentName } }
  );
}

export async function runHealthPrediction(
  clientId: string,
  data: { Agent_Names?: string[]; Include_Signals?: boolean } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.HEALTH_PREDICTION_RUN,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

// =============================================================================
// SYSTEM INTELLIGENCE SYNTHESIS
// =============================================================================

export interface CrossEngineCorrelation {
  Engine_A: string;
  Engine_B: string;
  Finding: string;
  Severity: 'info' | 'warning' | 'critical';
}

export interface IntelligenceInsight {
  Insight_ID: string;
  Title: string;
  Description: string;
  Severity: 'info' | 'warning' | 'critical';
  Engine_Source: string;
}

export interface IntelligenceSynthesis {
  Synthesis_Type: string;
  Insights: IntelligenceInsight[];
  Cross_Engine_Correlations: CrossEngineCorrelation[];
  Narrative: string;
  Confidence: number;
}

export async function getIntelligenceSynthesis(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.INTELLIGENCE_SYNTHESIS,
    'GET',
    null,
    { params: { Client_ID: clientId } }
  );
}

export async function runIntelligenceSynthesis(
  clientId: string,
  data: { Engines?: string[]; Depth?: 'quick' | 'standard' | 'deep' } = {}
): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.INTELLIGENCE_SYNTHESIS_RUN,
    'POST',
    { Client_ID: clientId, ...data }
  );
}

export async function getIntelligenceInsights(clientId: string): Promise<TimComboAPIResponse> {
  return Make_Authorized_API_Call(
    API_ENDPOINTS.ANALYTICS.INTELLIGENCE_INSIGHTS,
    'GET',
    null,
    { params: { Client_ID: clientId } }
  );
}
